import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";

/**
 * The appearance this person chose, if they chose one.
 *
 * `system` means **follow the device**, which is not the same as light: a
 * person who never opened the switcher keeps getting whatever their OS asks
 * for (`prefers-color-scheme`), including a change made later in the day.
 * (Spec 2026-09-07 candidate-b-fidelity, D18.)
 */
export type ThemePreference = "system" | "light" | "dark";

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;

/**
 * The i18n key for each of the three states. Shared by the Header's
 * `ThemeSwitcher` and the Appearance row on `/settings/agent`, so both
 * controls name the states identically.
 */
export const THEME_LABEL_KEYS = {
  system: "theme.followDevice",
  light: "theme.light",
  dark: "theme.dark",
} as const satisfies Record<ThemePreference, `theme.${string}`>;

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

interface PreferencesState {
  theme: ThemePreference;
  /** Choose an appearance, or pass `system` to follow the device again. */
  setTheme: (theme: ThemePreference) => void;
}

/**
 * Same shape as the language picker (`everreal-language` in localStorage,
 * `mobile/lib/core/locale/locale_preference.dart`): one key, per device
 * rather than per wallet. Sign-out clears only the auth store, so the choice
 * survives it — an appearance is a property of the person reading the
 * screen, not of the account they happen to be signed into.
 */
export const PREFERENCES_STORAGE_KEY = "detrust-preferences";

/**
 * A store that cannot answer reads as nothing stored — the same as never
 * having chosen. This is the availability side of the law, and it fails
 * open: the cost is a screen in the device's appearance instead of the
 * chosen one, which the person can still read their way out of. Throwing at
 * them over it would not be.
 *
 * A write the browser refuses costs the person their choice on the NEXT
 * visit and says so in the console — it must not cost them the appearance
 * they are looking at right now (the state has already changed by the time
 * persist reaches here).
 */
const failOpenStorage: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      return localStorage.getItem(name);
    } catch (error) {
      console.error("[preferences] unreadable; following the device:", error);
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      console.error("[preferences] not saved; it holds for this visit only:", error);
    }
  },
  removeItem: (name: string): void => {
    try {
      localStorage.removeItem(name);
    } catch (error) {
      console.error("[preferences] could not be cleared:", error);
    }
  },
};

/**
 * Preferences Store
 * Per-device preferences about the person reading the screen — today, the
 * appearance (D18). Nothing here is money; nothing here is per wallet.
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      storage: createJSONStorage(() => failOpenStorage),
      partialize: (state) => ({ theme: state.theme }),
      // A corrupt or foreign value reads as "follow the device", never as a
      // throw: the persisted shape is validated on the way in.
      merge: (persisted, current) => {
        const stored =
          persisted && typeof persisted === "object" && "theme" in persisted
            ? (persisted as { theme: unknown }).theme
            : undefined;
        return { ...current, theme: isThemePreference(stored) ? stored : "system" };
      },
      onRehydrateStorage: () => (_state, error) => {
        if (error) console.error("[preferences] unreadable; following the device:", error);
      },
    },
  ),
);
